import {observer} from 'mobx-react-lite'
import * as React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Tracking, TrackingEvent} from '@common/tracking'
import {LinkButton} from '@components/common/base/buttons'
import {ExtensionCommentsLink} from '@components/extensions-gallery/extension/partials/extension-comments-link'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {handleExtensionRate} from '../extension/extension-side-effects'
import {ExtensionStore} from '../extensions-store'
import {ExtensionActionButtons} from './partials/extension-action-buttons'
import {ExtensionMedia} from './partials/extension-media'
import {ExtensionRatingControl} from './partials/extension-rating-control'
import {ExtensionStatItem} from './partials/extension-stat-item'
import {ExtensionTags} from './partials/extension-tags'

const TwContainer = tw.div`
  flex
  flex-col
  py-16
  rounded-12
  border-border-base
  border-solid
  border-1
  shadow-sm
  bg-primary
`
const TwHeader = tw.div`
  flex
  justify-between
  cursor-default
  px-16
  mb-16
`

const TwHeaderTitle = tw.a<{onClick?: () => void}>`
  text-primary
  h2-semibold
  mr-24
  hover:text-primary
  ${p => (!p.onClick ? 'cursor-default' : '')}
`

const TwHeaderStats = tw.div`
  flex
  flex-wrap
  gap-12
  text-secondary
  mt-10
  min-h-[20px]
`

const TwHeaderActions = tw.div`
  flex
  flex-wrap
  shrink-0
  gap-8
  items-start
`

const TwDescription = tw.div`
  px-16

  text-primary
  text-sm-regular

  cursor-default
`

const TwFooter = tw.div`
  flex
  justify-between
  items-center
  w-full
  h-fit
  px-16
  mt-16
`

export interface ExtensionItemProps {
  extensionStore: ExtensionStore
  onOpenDetails: () => void
  onOpenSettings: () => void
}

export const ExtensionItem = observer((props: ExtensionItemProps) => {
  const {extensionStore, onOpenDetails, onOpenSettings} = props
  const isStandalone = globals.standalone
  const [commentLinkTooltip, setCommentLinkTooltip] = React.useState<
    string | undefined
  >(undefined)
  const tooltipTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>()
  const tooltipDelayRef = React.useRef<ReturnType<typeof setTimeout>>()

  const isTooltipDisabled = Boolean(commentLinkTooltip)

  const handleRatingChanges = (nextRating: number) => {
    handleExtensionRate(extensionStore, nextRating)

    Tracking.send(TrackingEvent.extensionsRateExtensionInList, {
      extensionId: extensionStore.id
    })
    const tooltip =
      nextRating >= 4
        ? '🔥 ' + Lang.msg.extensions.gallery.leaveGoodFeedback
        : '🤔 ' + Lang.msg.extensions.gallery.leaveBadFeedback
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current)
    }
    if (tooltipDelayRef.current) {
      clearTimeout(tooltipDelayRef.current)
    }
    tooltipDelayRef.current = setTimeout(() => {
      setCommentLinkTooltip(tooltip)
      tooltipTimeoutRef.current = setTimeout(() => {
        setCommentLinkTooltip(undefined)
      }, 5000)
    }, 600)
  }

  React.useEffect(() => {
    return () => {
      clearTimeout(tooltipTimeoutRef.current)
      clearTimeout(tooltipDelayRef.current)
    }
  }, [])
  return (
    <TwContainer>
      <TwHeader>
        <div>
          <TwHeaderTitle
            onClick={
              isStandalone
                ? undefined
                : () => {
                    onOpenDetails()
                    Tracking.send(TrackingEvent.extensionsClickOnExtensionTitle)
                  }
            }
          >
            {extensionStore.name}
          </TwHeaderTitle>
          {isStandalone ? null : (
            <TwHeaderStats>
              {extensionStore.canRate && (
                <ExtensionRatingControl
                  initialRating={extensionStore.currentUserRating}
                  isTooltipDisabled={isTooltipDisabled}
                  onRatingChanged={handleRatingChanges}
                />
              )}

              <ExtensionCommentsLink
                extensionStore={extensionStore}
                onOpenDetails={onOpenDetails}
                tooltipContent={commentLinkTooltip}
              />

              <ExtensionStatItem
                icon="IconStar"
                value={extensionStore.rating.toFixed(1)}
                tooltipContent={
                  isTooltipDisabled
                    ? undefined
                    : extensionStore.ratingsCount === 0
                      ? Lang.msg.extensions.ratings.emptyRating
                      : Lang.msg.ratingsCount(extensionStore.ratingsCount)
                }
              />

              <ExtensionStatItem
                icon="IconDownload"
                value={extensionStore.installsCount}
                tooltipContent={
                  isTooltipDisabled
                    ? undefined
                    : Lang.msg.extensions.installsCountHint
                }
              />
            </TwHeaderStats>
          )}
        </div>
        <TwHeaderActions>
          <ExtensionActionButtons
            extensionStore={extensionStore}
            onOpenSettings={onOpenSettings}
          />
        </TwHeaderActions>
      </TwHeader>

      {Boolean(extensionStore.description.length) && (
        <TwDescription
          className={cn(extensionStore.hasMedia ? 'mb-16' : '')}
          onClick={() => {
            Tracking.send(
              TrackingEvent.extensionsClickOnExtensionIsListDescriptionDeadClick
            )
          }}
        >
          {extensionStore.description}
        </TwDescription>
      )}

      {extensionStore.hasMedia && (
        <ExtensionMedia
          extensionId={extensionStore.id}
          media={extensionStore.media}
          onClickMediaItem={(_, index) => {
            Tracking.send(TrackingEvent.extensionsClickOnMediaInList, {index})
          }}
          px={16}
        />
      )}

      <TwFooter>
        {extensionStore.hasTags && (
          <ExtensionTags
            extension={extensionStore}
            onClickTag={tag => {
              Tracking.send(
                TrackingEvent.extensionsClickOnExtensionIsListTagDeadClick,
                {tag}
              )
            }}
          />
        )}

        {extensionStore.hasInstructionLink && (
          <a href={extensionStore.instructionLink} target="_blank">
            <LinkButton
              icon="IconInstructionLink"
              size="small"
              noPaddings
              iconPadding="4"
            >
              {Lang.msg.extensions.gallery.instruction}
            </LinkButton>
          </a>
        )}
      </TwFooter>
    </TwContainer>
  )
})
